import React, { useEffect, useRef, useState } from 'react';
import * as Player from './styles/player-style';
import { Cpds } from '@Cpds.Ui';
import { checkValue } from 'src/components/common/helper';
import { CpdsAspectRatioType, CpdsObjectFitType } from 'components/common/cpds/cpds-image/constants';
import { css } from 'styled-components';
import { ClickStreamEventType } from 'src/utils/event-wrapper';

interface YouTubePlayerProps {
  videoId: string;
  posterUrl?: string;
  playBtnColor?: string;
  playerVars?: {
    controls?: number; // Hide the control bar and the red play button
    modestbranding?: number; // Minimal branding
    rel?: number; // Disable related videos at the end
    showinfo?: number; // Hide video title and uploader info
    playsinline?: number;
  };
  altText?: string;
  trackClickVideo?: (arg: any) => void;
  eventCategory?: string;
  eventLocation?: string;
}

declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

const YouTubePlayer: React.FC<YouTubePlayerProps> = ({
  videoId,
  playerVars,
  posterUrl,
  playBtnColor,
  altText = 'youtube video',
  trackClickVideo,
  eventCategory,
  eventLocation
}) => {
  const playerRef = useRef<HTMLDivElement | null>(null);
  const [player, setPlayer] = useState<any>(null);
  const [playerState, setPlayerState] = useState<any>(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showThumbnail, setShowThumbnail] = useState(true);

  // Load the YouTube Iframe API
  useEffect(() => {
    // Polling function to wait for the YouTube API to be available
    const waitForYT = (attempts = 10) => {
      if (window.YT && window.YT.Player && playerRef.current) {
        // Initialize player once API is ready
        const newPlayer = new window.YT.Player(playerRef.current, {
          videoId,
          playerVars: { ...playerVars },
          events: {
            onReady: () => setPlayer(newPlayer),
            onStateChange: (event) => {
              setPlayerState(event.data);
              if (event.data === window.YT.PlayerState.PLAYING) {
                setIsPlaying(true);
              } else if (event.data === window.YT.PlayerState.PAUSED || event.data === window.YT.PlayerState.ENDED) {
                setIsPlaying(false);
              }
            },
          },
        });
      } else if (attempts > 0) {
        // Retry with a delay
        setTimeout(() => waitForYT(attempts - 1), 500);
      } else {
        console.error('YouTube API failed to load.');
      }
    };

    // Load YouTube API script only once
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.body.appendChild(tag);
    }

    // Start polling for the YouTube API readiness
    waitForYT();

    // Clean up player instance on component unmount
    return () => {
      if (player) player.destroy();
    };
  }, [videoId, playerVars]);

  const trackClickstream = () => {
    if (trackClickVideo) {
      const videoDuration = Math.round(player.getDuration()) || 0;
      const videoUrl = player.getVideoUrl();
      const videoTitle = videoUrl.split('/').pop() || 'Unknown Title';
  
      const clickstreamDataImage = {
        EVENT_NAME:
          playerState === window.YT.PlayerState.PLAYING
            ? 'video_play.selected'
            : 'video_pause.selected', // Correct condition
        EVENT_TYPE: ClickStreamEventType.INTERACTION,
        EVENT_CATEGORY: eventCategory, // Ensure this is defined
        EVENT_LOCATION: eventLocation, // Ensure this is defined
        VIDEO_LIST: {
          //video_title: videoTitle,
          video_id: videoTitle,
          video_url: videoUrl,
          video_duration: videoDuration,
          video_type: 'YouTube',
        },
      };
      trackClickVideo(JSON.stringify(clickstreamDataImage));
    }
  };

  // Toggle play and pause
  const handleTogglePlay = () => {
    if (isPlaying) {
      player?.pauseVideo();
    } else {
      player?.playVideo();
      setShowThumbnail(false);
    }
    trackClickstream();
  };

  return (
    <>
      <Player.Container aria-label={altText} className="v3-breaker-player-ref" ref={playerRef} />
      <Player.Overlay playerState={playerState}>
        {checkValue(posterUrl) && (
          <Cpds.Image
            alt={altText}
            src={posterUrl}
            aspectRatio={CpdsAspectRatioType.CPDS_16x9}
            objectFit={CpdsObjectFitType.CPDS_COVER}
            styleOverrides={css`
              width: 100%;
              height: 100%;
              object-fit: cover;
              opacity: ${showThumbnail ? 1 : 0};
              transition: opacity 0.5s ease-in-out;
            `}
          />
        )}
      </Player.Overlay>
      <Cpds.PlayButton.MutedControls
        togglePlay={handleTogglePlay}
        isPlaying={isPlaying}
        playBtnColor={playBtnColor}
        playerState={playerState}
      />
    </>
  );
};
export default YouTubePlayer;
